import { useMemo, useState } from 'react';
import { usePlan } from '../store/planStore.js';
import { useMeals } from '../recipes/useMeals.js';
import { householdPortions } from '../../domain/portions.js';
import { round2 } from '../../domain/units.js';
import type { Meal, MealTag } from '../../domain/types.js';
import { Sheet } from '../components/Sheet.js';
import { MealCard } from '../components/MealCard.js';
import { MAX_MINUTES, defaultFilterFor, fitsEffort } from './effort.js';
import { weekdayOf, isoDate } from '../../domain/calendar.js';
import { RecipeForm } from './RecipeForm.js';
import type { CustomMeal } from '../store/library.js';

type Filter = 'all' | 'favourites' | 'yours' | MealTag;

const FILTERS: { key: Filter; label: string }[] = [
  { key: 'favourites', label: '★ Favourites' },
  { key: 'yours', label: 'Yours' },
  { key: 'budget', label: 'Budget' },
  { key: 'batch', label: 'Batch cook' },
  { key: 'quick', label: 'Under 30 min' },
  { key: 'veggie', label: 'Veggie' },
  { key: 'mild', label: 'Mild' },
];

export function MealPicker({
  date,
  onClose,
}: {
  date: string;
  onClose: () => void;
}): React.JSX.Element {
  const { ready, plan, settings, library, setDinner, clearDinner, toggleFavourite, saveRecipe, archiveRecipe } = usePlan();
  const catalogue = useMeals();
  const nightEffort = settings.effort[weekdayOf(isoDate(date))];
  const household = ready ? householdPortions(settings.people) : 0;

  // What is already planned for this date, if anything. Read fresh on every
  // render (not just at mount) so it stays true to the store, but only ever
  // used to SEED the state below — the sheet is a fresh component instance
  // each time it opens (see App.tsx), so a lazy initialiser runs exactly once
  // per open.
  const existingDinner = plan.dinners.find((d) => d.date === date) ?? null;
  const existingMeal = existingDinner
    ? (catalogue.byId.get(existingDinner.mealId) ?? null)
    : null;

  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>(() => defaultFilterFor(nightEffort));
  // B1: a day that already holds a meal must open ON that meal, already
  // marked up as it was cooked. Defaulting to `null`/1/0 regardless of what
  // is stored is how "cooks double" quietly became "one night" the moment
  // someone reopened Friday to admire it and tapped "Plan it" again.
  const [chosen, setChosen] = useState<Meal | null>(() => existingMeal);
  const [spansDays, setSpansDays] = useState(() => existingDinner?.spansDays ?? 1);
  const [extraPortions, setExtraPortions] = useState(() =>
    existingDinner?.portions != null ? round2(existingDinner.portions - household) : 0,
  );

  const [form, setForm] = useState<{ editing: CustomMeal | null } | null>(null);
  // The id the remove-confirmation was asked about — never a bare boolean,
  // so it cannot carry over to a different recipe.
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);
  const chosenCustom = chosen ? (library.customMeals.find((m) => m.id === chosen.id) ?? null) : null;
  const confirmRemove = chosenCustom !== null && confirmRemoveId === chosenCustom.id;

  // Per-night portions — what actually gets stored. The engine multiplies
  // this by spansDays itself (dinnerPortions()); storing the multiplied
  // figure here would triple the over-buy for every batch cook.
  const perNightPortions = round2(household + extraPortions);
  const totalPortions = round2(perNightPortions * spansDays);
  const spanWord = spansDays === 1 ? 'one night' : spansDays === 2 ? 'two nights' : 'three nights';

  const meals = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matched = catalogue.pickable.filter((meal) => {
      if (q && !meal.name.toLowerCase().includes(q)) return false;
      if (filter === 'all') return true;
      if (filter === 'favourites') return library.favourites.includes(meal.id);
      if (filter === 'yours') return catalogue.customIds.has(meal.id);
      // B5: "Under 30 min" means minutes, not the `quick` tag — leek & potato
      // soup is 30 minutes but carries no `quick` tag, so the tag-based check
      // hid the one low-effort veggie soup on exactly the nights it is for.
      // `effort.ts` owns the threshold so the two checks cannot drift apart.
      if (filter === 'quick') return meal.effortMinutes <= MAX_MINUTES.low;
      return meal.tags.includes(filter);
    });
    // Favourites float to the top, permanently.
    return matched.sort((a, b) => {
      const aFav = library.favourites.includes(a.id) ? 0 : 1;
      const bFav = library.favourites.includes(b.id) ? 0 : 1;
      return aFav - bFav || a.name.localeCompare(b.name);
    });
  }, [query, filter, library.favourites, catalogue]);

  if (!ready) return <p>Loading</p>;

  return (
    <Sheet open title={form ? 'Your recipe' : 'Pick a meal'} onClose={onClose}>
      {form ? (
        <RecipeForm
          editing={form.editing}
          onCancel={() => setForm(null)}
          onSaved={(meal) => {
            saveRecipe(meal);
            setConfirmRemoveId(null);
            setForm(null);
            setChosen(meal);
          }}
        />
      ) : chosen ? (
        <div className="confirm">
          {/* B4: picking the wrong meal shouldn't cost the whole sheet (and
              the search filter with it). */}
          <button type="button" className="ghost" onClick={() => { setConfirmRemoveId(null); setChosen(null); }}>
            &larr; Back to meals
          </button>
          <h3>{chosen.name}</h3>
          {/* B3: what actually gets cooked is portions × spansDays. Showing
              the per-night figure alone understated a three-night batch by
              3×, and invited "correcting" it upward — which the engine then
              multiplies by spansDays AGAIN, silently tripling the over-buy. */}
          <p className="confirm-portions">
            Cooking {totalPortions} portions &mdash; {spanWord}
          </p>
          <div className="confirm-row">
            <button
              type="button"
              onClick={() =>
                setExtraPortions((n) => Math.max(round2(1 - household), round2(n - 1)))
              }
            >
              Fewer portions
            </button>
            <button type="button" onClick={() => setExtraPortions((n) => round2(n + 1))}>
              More portions
            </button>
          </div>
          <div className="confirm-row">
            {([1, 2, 3] as const).map((n) => (
              <button
                key={n}
                type="button"
                aria-pressed={spansDays === n}
                onClick={() => setSpansDays(n)}
              >
                {n === 1 ? 'One night' : n === 2 ? 'Cooks double' : 'Batch, three nights'}
              </button>
            ))}
          </div>
          {!fitsEffort(chosen, nightEffort) ? (
            <p className="nudge">
              {chosen.effortMinutes} minutes on a night you marked {nightEffort}. Fine if
              you have the time — this is the night a plan usually turns into a takeaway.
            </p>
          ) : null}
          <button
            type="button"
            className="primary"
            onClick={() => {
              setDinner(
                date,
                chosen.id,
                spansDays,
                extraPortions === 0 ? null : perNightPortions,
              );
              onClose();
            }}
          >
            Plan it
          </button>
          {/* B2: clearDinner had no caller anywhere, so a planned dinner
              could never be unplanned — offer it whenever this date already
              had one when the sheet opened, regardless of what is chosen now. */}
          {existingDinner ? (
            <button
              type="button"
              className="ghost"
              onClick={() => {
                clearDinner(date);
                onClose();
              }}
            >
              Not cooking this night
            </button>
          ) : null}
          {chosenCustom ? (
            <div className="confirm-row">
              <button type="button" onClick={() => { setConfirmRemoveId(null); setForm({ editing: chosenCustom }); }}>Edit recipe</button>
              {confirmRemove ? (
                <>
                  <p className="nudge">
                    Remove it from your recipes? It stays on any week it&rsquo;s already planned.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      archiveRecipe(chosenCustom.id);
                      setConfirmRemoveId(null);
                      setChosen(null);
                    }}
                  >
                    Yes, remove it
                  </button>
                </>
              ) : (
                <button type="button" className="ghost" onClick={() => setConfirmRemoveId(chosenCustom.id)}>
                  Remove recipe
                </button>
              )}
            </div>
          ) : null}
        </div>
      ) : (
        <>
          <button type="button" className="ghost" onClick={() => setForm({ editing: null })}>
            ＋ Add a recipe
          </button>
          <input
            className="search"
            type="search"
            placeholder="Search meals"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <div className="chips">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                aria-pressed={filter === f.key}
                onClick={() => setFilter((current) => (current === f.key ? 'all' : f.key))}
              >
                {f.label}
              </button>
            ))}
          </div>
          {meals.length === 0 ? (
            <p className="empty-note">No meals match that. Try a different word or clear the filters.</p>
          ) : (
            <div className="meal-list">
              {meals.map((meal) => (
                <MealCard
                  key={meal.id}
                  meal={meal}
                  starred={library.favourites.includes(meal.id)}
                  own={catalogue.customIds.has(meal.id)}
                  onToggleStar={() => toggleFavourite(meal.id)}
                  onPick={() => setChosen(meal)}
                />
              ))}
            </div>
          )}
        </>
      )}
    </Sheet>
  );
}
